<script setup lang="ts">
    import { ref } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import { fileIconForMime, isInlineViewable } from 'models'
    import {
        useChatbotStore,
        useDialogConfirm,
        type ChatbotFile,
    } from 'composables'

    const props = defineProps<{ agentId: string }>()

    const { t: $t, locale } = useI18n({ useScope: 'global' })
    const { openDialogConfirm } = useDialogConfirm()

    const store = useChatbotStore(props.agentId)
    const { chatFiles, isLoadingChatFiles } = storeToRefs(store)

    const deletingId = ref<string | null>(null)

    const openFile = (file: ChatbotFile, download = false) => {
        const inline = !download && isInlineViewable(file.mimeType)
        window.open(
            store.getChatFileUrl(file.documentId, inline),
            '_blank',
            'noopener',
        )
    }

    const handleExclude = async (file: ChatbotFile) => {
        const proceed = await openDialogConfirm({
            title: $t('userFiles.excludeTitle'),
            questionLabel: $t('userFiles.excludeConfirm'),
        })
        if (!proceed) {
            return
        }
        deletingId.value = file.documentId
        try {
            await store.deleteChatFile(file.documentId)
        } catch {
            // surfaced by the host's error handling
        } finally {
            deletingId.value = null
        }
    }

    const formatDate = (iso: string): string =>
        new Date(iso).toLocaleDateString(locale.value, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 relative">
        <!-- Loading skeleton -->
        <div
            v-if="isLoadingChatFiles && chatFiles.length === 0"
            class="flex flex-col gap-8 p-16 vv-skeleton">
            <div
                v-for="i in 4"
                :key="i"
                class="vv-skeleton__item rounded-md h-56" />
        </div>

        <!-- Empty state -->
        <div
            v-else-if="chatFiles.length === 0"
            class="flex flex-col flex-1 items-center justify-center gap-12 p-24 text-center">
            <div
                class="w-64 h-64 rounded-full bg-surface-1 border border-surface-3 flex items-center justify-center">
                <VvIcon
                    name="ri:folder-open-line"
                    class="w-28 h-28 text-word-4" />
            </div>
            <span class="text-14 text-word-3 leading-relaxed">
                {{ $t('userFiles.chatFilesEmpty') }}
            </span>
        </div>

        <!-- File list -->
        <ul v-else class="pk-chatbot-files">
            <li
                v-for="file in chatFiles"
                :key="file.documentId"
                class="pk-chatbot-files__item">
                <span class="pk-chatbot-files__icon">
                    <VvIcon :name="fileIconForMime(file.mimeType)" />
                </span>
                <button
                    type="button"
                    class="pk-chatbot-files__main"
                    :title="$t('userFiles.open')"
                    @click="openFile(file)">
                    <span class="pk-chatbot-files__name">
                        {{ file.name || $t('userFiles.untitled') }}
                    </span>
                    <span class="pk-chatbot-files__date">
                        {{ formatDate(file.createdAt) }}
                    </span>
                </button>
                <div class="pk-chatbot-files__actions">
                    <VvButton
                        modifiers="action-quiet xs"
                        icon="ri:download-line"
                        :title="$t('action.download')"
                        @click="openFile(file, true)" />
                    <VvButton
                        modifiers="action-quiet-danger xs"
                        icon="ri:delete-bin-line"
                        :title="$t('userFiles.exclude')"
                        :loading="deletingId === file.documentId"
                        :disabled="deletingId === file.documentId"
                        @click="handleExclude(file)" />
                </div>
            </li>
        </ul>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-files {
        display: flex;
        flex-direction: column;
        list-style: none;
        margin: 0;
        padding: var(--spacing-8);
        overflow-y: auto;

        &__item {
            display: flex;
            align-items: center;
            gap: var(--spacing-10);
            padding: var(--spacing-8) var(--spacing-10);
            border-radius: var(--rounded-md);
            transition: var(--transition-colors);

            // Separator between files
            & + & {
                border-top: 1px solid var(--color-surface-3);
            }

            &:hover {
                background-color: var(--color-surface-2);
            }
        }

        &__icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: var(--spacing-36);
            height: var(--spacing-36);
            flex-shrink: 0;
            border-radius: var(--rounded-md);
            background-color: var(--color-surface-1);
            color: var(--color-word-3);
        }

        &__main {
            display: flex;
            flex-direction: column;
            min-width: 0;
            flex: 1;
            text-align: left;
            background: none;
            border: 0;
            padding: 0;
            cursor: pointer;
        }

        &__name {
            font-size: var(--text-14);
            color: var(--color-word-1);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &__date {
            font-size: var(--text-12);
            color: var(--color-word-3);
        }

        &__actions {
            display: flex;
            align-items: center;
            gap: var(--spacing-2);
            flex-shrink: 0;
        }
    }
</style>
